import { useState, useEffect } from 'react';
import { X, Sparkles, Link2, Building2, User } from 'lucide-react';
import axios from 'axios';
import { taskApi } from '../../../shared/services/taskApi';
import { api } from '@/shared/services/api';
import { Spinner } from '@/shared/components/Spinner';
import { cn } from '@/shared/utils/cn';
import { todayIso } from '@/shared/utils/today';
import type {
  CreateTaskRequest,
  TaskType,
  TaskPriority,
  TaskDescriptionSuggestion,
  GenerateTaskDescriptionRequest
} from '../../../types/task';

interface AssignableUser {
  userId: string;
  displayName: string;
}

interface CreateTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTaskCreated?: () => void;
  feedItemId?: number;
  feedItemTitle?: string;
  feedItemDescription?: string;
  clientId?: number;
  clientName?: string;
}

export function CreateTaskModal({
  isOpen,
  onClose,
  onTaskCreated,
  feedItemId: initialFeedItemId,
  feedItemTitle,
  feedItemDescription,
  clientId: initialClientId,
  clientName,
}: CreateTaskModalProps) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'Task' as TaskType,
    priority: 'Medium' as TaskPriority,
    storyPoints: undefined as number | undefined,
    labels: [] as string[],
    acceptanceCriteria: '',
    dueDate: '',
    feedItemId: initialFeedItemId || undefined as number | undefined,
    clientId: initialClientId || undefined as number | undefined,
    assignedToUserId: undefined as string | undefined,
  });

  const [isGenerating, setIsGenerating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiSuggestion, setAiSuggestion] = useState<TaskDescriptionSuggestion | null>(null);

  // Lists for dropdowns
  interface FeedItemOption { id: number; title: string; }
  interface ClientOption { id: number; name: string; }

  const [feedItems, setFeedItems] = useState<FeedItemOption[]>([]);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [users, setUsers] = useState<AssignableUser[]>([]);
  const [loadingLists, setLoadingLists] = useState(true);

  useEffect(() => {
    if (isOpen) {
      loadDropdownData();
    }
  }, [isOpen]);

  const loadDropdownData = async () => {
    setLoadingLists(true);
    try {
      const [feedItemsRes, clientsRes, usersRes] = await Promise.all([
        api.get('/feeditems?pageSize=100'),
        api.get('/clients'),
        api.get('/auth/assignable-users'),
      ]);

      setFeedItems(feedItemsRes.data.items || feedItemsRes.data || []);
      setClients(clientsRes.data || []);
      setUsers(usersRes.data || []);
    } catch (err) {
      console.error('Error loading dropdown data:', err);
    } finally {
      setLoadingLists(false);
    }
  };

  if (!isOpen) return null;

  const handleGenerateWithAI = async () => {
    if (!feedItemTitle || !feedItemDescription) {
      setError('Feed item details are required for AI generation');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const request: GenerateTaskDescriptionRequest = {
        feedItemTitle,
        feedItemDescription,
        clientName,
      };

      const suggestion = await taskApi.generateTaskDescription(request);
      setAiSuggestion(suggestion);

      // Auto-fill form with AI suggestions
      setFormData(prev => ({
        ...prev,
        title: suggestion.title,
        description: suggestion.description,
        priority: suggestion.priority,
        storyPoints: suggestion.storyPoints,
        labels: suggestion.labels || [],
        acceptanceCriteria: suggestion.acceptanceCriteria || '',
      }));
    } catch (err) {
      setError('Failed to generate task description. Make sure Ollama is running.');
      console.error('AI generation error:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // SC-264: fail fast rather than round-tripping to the server's validator. Compared as
    // yyyy-MM-dd strings so this matches the user's own calendar day, not a UTC instant.
    if (formData.dueDate && formData.dueDate < todayIso()) {
      setError('Due date cannot be in the past.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const request: CreateTaskRequest = {
        title: formData.title,
        description: formData.description,
        type: formData.type,
        priority: formData.priority,
        feedItemId: formData.feedItemId,
        clientId: formData.clientId,
        assignedToUserId: formData.assignedToUserId,
        storyPoints: formData.storyPoints,
        labels: formData.labels.length > 0 ? formData.labels : undefined,
        acceptanceCriteria: formData.acceptanceCriteria || undefined,
        dueDate: formData.dueDate ? formData.dueDate : undefined,
        isAiGenerated: !!aiSuggestion,
        aiGenerationPrompt: aiSuggestion?.generationPrompt,
      };

      await taskApi.createTask(request);
      onTaskCreated?.();
      onClose();
    } catch (err) {
      const errorMessage = axios.isAxiosError(err)
        ? (err.response?.data?.error ?? err.response?.data?.title ?? 'Failed to create task')
        : 'Failed to create task';
      setError(errorMessage);
      console.error('Task creation error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLabelInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && e.currentTarget.value.trim()) {
      e.preventDefault();
      const newLabel = e.currentTarget.value.trim();
      if (!formData.labels.includes(newLabel)) {
        setFormData(prev => ({
          ...prev,
          labels: [...prev.labels, newLabel],
        }));
      }
      e.currentTarget.value = '';
    }
  };

  const removeLabel = (label: string) => {
    setFormData(prev => ({
      ...prev,
      labels: prev.labels.filter(l => l !== label),
    }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-fade-in">
      <div className="surface-overlay flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl shadow-modal animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-xl font-semibold text-foreground">Create New Task</h2>
          <button
            type="button"
            onClick={onClose}
            className="btn-icon btn-ghost"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* AI Generation Button */}
          {feedItemTitle && feedItemDescription && (
            <div className="border-b border-border bg-primary/5 px-6 py-4">
              <button
                type="button"
                onClick={handleGenerateWithAI}
                disabled={isGenerating}
                className="btn-primary w-full"
              >
                {isGenerating ? (
                  <>
                    <Spinner className="text-primary-foreground" />
                    Generating with AI…
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    Generate Task Description with AI
                  </>
                )}
              </button>
              {aiSuggestion && (
                <p className="mt-2 flex items-center justify-center gap-1.5 text-sm text-primary">
                  <Sparkles className="h-3.5 w-3.5" />
                  AI-generated description loaded! You can edit it below.
                </p>
              )}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6 p-6">
            {error && (
              <div className="rounded-md border border-danger/30 bg-danger-subtle p-3">
                <p className="text-sm text-danger-foreground">{error}</p>
              </div>
            )}

            {/* Title */}
            <div>
              <label htmlFor="task-title" className="label">
                Title *
              </label>
              <input
                id="task-title"
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="input mt-1.5"
                placeholder="Enter task title"
              />
            </div>

            {/* Type and Priority */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="task-type" className="label">
                  Type
                </label>
                <select
                  id="task-type"
                  value={formData.type}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as TaskType }))}
                  className="input mt-1.5"
                >
                  <option value="Task">Task</option>
                  <option value="Bug">Bug</option>
                  <option value="Story">Story</option>
                  <option value="Epic">Epic</option>
                  <option value="SubTask">SubTask</option>
                </select>
              </div>

              <div>
                <label htmlFor="task-priority" className="label">
                  Priority
                </label>
                <select
                  id="task-priority"
                  value={formData.priority}
                  onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as TaskPriority }))}
                  className="input mt-1.5"
                >
                  <option value="Lowest">Lowest</option>
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Highest">Highest</option>
                </select>
              </div>
            </div>

            {/* Feed Item, Client, and Assignee */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label htmlFor="task-feeditem" className="label flex items-center gap-1.5">
                  <Link2 className="h-3.5 w-3.5 text-muted-foreground" />
                  Feed Item
                </label>
                <select
                  id="task-feeditem"
                  value={formData.feedItemId || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    feedItemId: e.target.value ? parseInt(e.target.value) : undefined
                  }))}
                  className="input mt-1.5 text-sm"
                  disabled={loadingLists}
                >
                  <option value="">None</option>
                  {feedItems.map(item => (
                    <option key={item.id} value={item.id}>
                      {item.title?.substring(0, 40)}{item.title?.length > 40 ? '...' : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="task-client" className="label flex items-center gap-1.5">
                  <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                  Client
                </label>
                <select
                  id="task-client"
                  value={formData.clientId || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    clientId: e.target.value ? parseInt(e.target.value) : undefined
                  }))}
                  className="input mt-1.5 text-sm"
                  disabled={loadingLists}
                >
                  <option value="">None</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="task-assignee" className="label flex items-center gap-1.5">
                  <User className="h-3.5 w-3.5 text-muted-foreground" />
                  Assign To
                </label>
                <select
                  id="task-assignee"
                  value={formData.assignedToUserId || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    assignedToUserId: e.target.value || undefined
                  }))}
                  className="input mt-1.5 text-sm"
                  disabled={loadingLists}
                >
                  <option value="">Me (default)</option>
                  {users.map(user => (
                    <option key={user.userId} value={user.userId}>
                      {user.displayName || user.userId}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label htmlFor="task-description" className="label">
                Description *
              </label>
              <textarea
                id="task-description"
                required
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={6}
                className="input mt-1.5"
                placeholder="Describe the task in detail..."
              />
            </div>

            {/* Acceptance Criteria */}
            <div>
              <label htmlFor="task-criteria" className="label">
                Acceptance Criteria
              </label>
              <textarea
                id="task-criteria"
                value={formData.acceptanceCriteria}
                onChange={(e) => setFormData(prev => ({ ...prev, acceptanceCriteria: e.target.value }))}
                rows={4}
                className="input mt-1.5"
                placeholder="- Criterion 1&#10;- Criterion 2&#10;- Criterion 3"
              />
            </div>

            {/* Story Points and Due Date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="task-points" className="label">
                  Story Points
                </label>
                <select
                  id="task-points"
                  value={formData.storyPoints || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    storyPoints: e.target.value ? parseInt(e.target.value) : undefined
                  }))}
                  className="input mt-1.5"
                >
                  <option value="">Not set</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="5">5</option>
                  <option value="8">8</option>
                  <option value="13">13</option>
                </select>
              </div>

              <div>
                <label htmlFor="task-duedate" className="label">
                  Due Date
                </label>
                <input
                  id="task-duedate"
                  type="date"
                  value={formData.dueDate}
                  // SC-264: also stops the past being reachable in the date picker.
                  min={todayIso()}
                  onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                  className="input mt-1.5"
                />
              </div>
            </div>

            {/* Labels */}
            <div>
              <label htmlFor="task-labels" className="label">
                Labels
              </label>
              <input
                id="task-labels"
                type="text"
                onKeyDown={handleLabelInput}
                className="input mt-1.5"
                placeholder="Type label and press Enter"
              />
              {formData.labels.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {formData.labels.map(label => (
                    <span
                      key={label}
                      className="badge-brand inline-flex items-center gap-1"
                    >
                      {label}
                      <button
                        type="button"
                        onClick={() => removeLabel(label)}
                        className="transition-colors hover:text-foreground"
                        aria-label={`Remove ${label}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Context Info */}
            {(clientName || feedItemTitle) && (
              <div className="rounded-lg border border-border bg-muted/40 p-4">
                <h3 className="mb-2 text-sm font-medium text-foreground">Context</h3>
                {clientName && (
                  <p className="text-sm text-muted-foreground">
                    Client: <span className="font-medium text-foreground">{clientName}</span>
                  </p>
                )}
                {feedItemTitle && (
                  <p className="text-sm text-muted-foreground">
                    Related to: <span className="font-medium text-foreground">{feedItemTitle}</span>
                  </p>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 border-t border-border pt-4">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={cn('btn-primary', isSubmitting && 'cursor-not-allowed')}
              >
                {isSubmitting ? (
                  <>
                    <Spinner className="text-primary-foreground" />
                    Creating…
                  </>
                ) : (
                  'Create Task'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
